import type { SupabaseClient } from '../supabase/client';
import type {
  StorefrontContentLocale,
  StorefrontNewsletterConsentVersion,
  StorefrontNewsletterContactType,
  StorefrontNewsletterSubscriberAdminRow,
  StorefrontNewsletterSubscriberStatus,
} from '../types/storefront-engine';

interface StorefrontNewsletterAdminRpcClient {
  rpc(
    name: string,
    args: Record<string, unknown>,
  ): Promise<{ readonly data: unknown; readonly error: unknown }>;
}

export interface ListStorefrontNewsletterSubscribersForAdminInput {
  readonly storeId: string;
  readonly limit?: number;
}

export interface SuppressStorefrontNewsletterSubscriberForAdminInput {
  readonly storeId: string;
  readonly subscriberId: string;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function parseRows(value: unknown): UnknownRecord[] {
  return Array.isArray(value) ? value.map(asRecord) : [];
}

function isContactType(value: unknown): value is StorefrontNewsletterContactType {
  return value === 'email' || value === 'phone';
}

function isStatus(value: unknown): value is StorefrontNewsletterSubscriberStatus {
  return value === 'active' || value === 'suppressed';
}

function isLocale(value: unknown): value is StorefrontContentLocale {
  return value === 'ar' || value === 'he' || value === 'en';
}

function isConsentVersion(value: unknown): value is StorefrontNewsletterConsentVersion {
  return value === 'newsletter-consent-v1';
}

function toIsoString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
}

function toSubscriberRow(row: UnknownRecord): StorefrontNewsletterSubscriberAdminRow | null {
  const id = typeof row.id === 'string' ? row.id : null;
  const contactType = isContactType(row.contactType) ? row.contactType : null;
  const contactValue = typeof row.contactValue === 'string' ? row.contactValue : null;
  const status = isStatus(row.status) ? row.status : null;
  const locale = isLocale(row.locale) ? row.locale : 'ar';
  const sourceSectionId =
    typeof row.sourceSectionId === 'string' && row.sourceSectionId.trim() !== ''
      ? row.sourceSectionId
      : null;
  const consentVersion = isConsentVersion(row.consentVersion) ? row.consentVersion : null;
  const consentedAt = toIsoString(row.consentedAt);
  const createdAt = toIsoString(row.createdAt);
  const updatedAt = toIsoString(row.updatedAt);
  const suppressedAt =
    row.suppressedAt === null || row.suppressedAt === undefined
      ? null
      : toIsoString(row.suppressedAt);
  const suppressedAtInvalid =
    row.suppressedAt !== null && row.suppressedAt !== undefined && suppressedAt === null;
  if (
    !id ||
    !contactType ||
    !contactValue ||
    !status ||
    !sourceSectionId ||
    !consentVersion ||
    !consentedAt ||
    !createdAt ||
    !updatedAt ||
    suppressedAtInvalid
  ) {
    return null;
  }
  return {
    id,
    contactType,
    contactValue,
    status,
    locale,
    sourceSectionId,
    consentVersion,
    consentedAt,
    createdAt,
    updatedAt,
    suppressedAt,
  };
}

async function invokeNewsletterAdminRpc(
  client: SupabaseClient,
  name: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  const rpcClient = client as unknown as StorefrontNewsletterAdminRpcClient;
  const { data, error } = await rpcClient.rpc(name, args);
  if (error) throw error;
  return data;
}

export async function listStorefrontNewsletterSubscribersForAdmin(
  client: SupabaseClient,
  input: ListStorefrontNewsletterSubscribersForAdminInput,
): Promise<StorefrontNewsletterSubscriberAdminRow[]> {
  const data = await invokeNewsletterAdminRpc(
    client,
    'list_storefront_newsletter_subscribers_for_admin',
    {
      target_store_id: input.storeId,
      page_limit: input.limit ?? 50,
    },
  );
  return parseRows(data).flatMap((row) => {
    const subscriber = toSubscriberRow(row);
    return subscriber ? [subscriber] : [];
  });
}

export async function suppressStorefrontNewsletterSubscriberForAdmin(
  client: SupabaseClient,
  input: SuppressStorefrontNewsletterSubscriberForAdminInput,
): Promise<StorefrontNewsletterSubscriberAdminRow> {
  const subscriber = toSubscriberRow(
    asRecord(
      await invokeNewsletterAdminRpc(
        client,
        'suppress_storefront_newsletter_subscriber_for_admin',
        {
          target_store_id: input.storeId,
          subscriber_id: input.subscriberId,
        },
      ),
    ),
  );
  if (!subscriber) throw new Error('invalid newsletter subscriber response');
  return subscriber;
}

const SPREADSHEET_FORMULA_PREFIX_RE = /^\s*[=+@-]/;
const SPREADSHEET_CONTROL_PREFIX_RE = /^\s*[\t\r\n]/;

function neutralizeSpreadsheetFormula(value: string): string {
  return SPREADSHEET_FORMULA_PREFIX_RE.test(value) || SPREADSHEET_CONTROL_PREFIX_RE.test(value)
    ? `'${value}`
    : value;
}

function csvCell(value: string | null): string {
  const text = value ?? '';
  const safeText = neutralizeSpreadsheetFormula(text);
  return /[",\n\r]/.test(safeText) ? `"${safeText.replaceAll('"', '""')}"` : safeText;
}

export function buildStorefrontNewsletterSubscribersCsv(
  rows: readonly StorefrontNewsletterSubscriberAdminRow[],
): string {
  const header = [
    'contact_type',
    'contact_value',
    'status',
    'locale',
    'source_section_id',
    'consent_version',
    'consented_at',
    'created_at',
    'updated_at',
    'suppressed_at',
  ];
  const body = rows.map((row) =>
    [
      row.contactType,
      row.contactValue,
      row.status,
      row.locale,
      row.sourceSectionId,
      row.consentVersion,
      row.consentedAt,
      row.createdAt,
      row.updatedAt,
      row.suppressedAt,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...body].join('\n');
}

export const storefrontNewsletterSubscribersAdminService = {
  list: listStorefrontNewsletterSubscribersForAdmin,
  suppress: suppressStorefrontNewsletterSubscriberForAdmin,
  buildCsv: buildStorefrontNewsletterSubscribersCsv,
};
